/**
 * Nursing homes domain — React Query hooks.
 *
 * Post-FIR-6997 the nursing homes table reads/mutates through the unified
 * opportunities engine (`services/opportunities`, `common_table_actions`),
 * so this domain's list/summary hooks and wrapped bulk mutations were
 * removed as dead code. What remains is the detail hook the nursing home
 * detail pages consume.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import client from '../apiClient'

import type {
  GetNursingHomeQueryResponse,
  GetNursingHome404,
} from './legacyDetailTypes'

export type {
  GetNursingHomeQueryResponse,
  GetNursingHomePathParams,
} from './legacyDetailTypes'

/**
 * Get nursing home detail.
 *
 * Hand-written wrapper around the generated `getNursingHome` client because
 * the legacy hook skips retries on 404 — without that predicate, React
 * Query retries 3× before surrendering, which delays "not found" feedback
 * in the UI. The wrapper accepts an optional id (so callers can pass
 * `undefined` before route resolution) and disables the query until the
 * id arrives.
 */
export function useGetNursingHome(
  nursingHomeId?: number | string,
  options: {
    query?: Omit<
      UseQueryOptions<
        GetNursingHomeQueryResponse,
        AxiosError<GetNursingHome404>
      >,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  // The backend route is `<str:nursinghome_id>`; callers pass the numeric
  // ContactCompany id, so coerce to the string the generated client expects.
  const id = nursingHomeId != null ? String(nursingHomeId) : undefined
  return useQuery<GetNursingHomeQueryResponse, AxiosError<GetNursingHome404>>({
    queryKey:
      id !== undefined ? ['nursinghome', id] : ['nursinghome', nursingHomeId],
    // FIR-7878: unified chain detail endpoint (byte-identical response).
    queryFn: async ({ signal }) =>
      (
        await client<GetNursingHomeQueryResponse>({
          method: 'GET',
          url: `/api/v1/opportunities/detail/${id}/`,
          params: { target_type: 'nursing_home' },
          signal,
        })
      ).data,
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}
